import { useState, useCallback } from 'react'

// --- helpers ---
const toNum = v => (v === '' || v == null ? 0 : Number(v)) || 0
const toYMD = (d = new Date()) => d.toISOString().slice(0, 10)
const addDays = (d, n) => {
	const dt = new Date(d)
	dt.setDate(dt.getDate() + n)
	return toYMD(dt)
}

const recalcRow = row => {
	const price = toNum(row.price)
	const count = toNum(row.count)
	const subtotal = +(price * count).toFixed(2)
	const taxPercent = toNum(row.taxPercent)
	const base = price * count
	const taxValue = +((base * taxPercent) / 100).toFixed(2)
	const total = +(base + taxValue).toFixed(2)
	return { ...row, subtotal, taxValue, total }
}

const recalcAll = (rows, discountPercent) => {
	const subtotal = +rows.reduce((s, r) => s + (r.subtotal || 0), 0).toFixed(2)
	let discountValue = +((subtotal * discountPercent)/100).toFixed(2) || 0;
	let subtotalAfterDiscount = +(subtotal - discountValue).toFixed(2);

	const taxTotal = +rows.reduce((s, r) => s + (r.taxValue || 0), 0).toFixed(2)
	let taxDiscountValue = +((taxTotal * discountPercent)/100).toFixed(2) || 0;
	let taxTotalAfterDiscount = +(taxTotal - taxDiscountValue).toFixed(2);
	
	let total = ((subtotalAfterDiscount + taxTotalAfterDiscount)).toFixed(2);

	return { subtotal, taxTotal:taxTotalAfterDiscount, discountValue, total }
};

const DEFAULT_EMPTY_ROW = {
	name: '',
	sku: '',
	article_number: '',
	price: '',
	count: '1',
	taxPercent: '5',
	taxValue: 0,
	total: 0,
	productId: '',
};

/**
 * useInvoiceFormState
 * Unified hook for Add + Edit invoice form
 */
export default function useInvoiceFormState(initialForm, emptyRow = DEFAULT_EMPTY_ROW) {
	const baseForm = initialForm || {
		customerId: '',
		customerName: '',
		invoiceDate: toYMD(),
		discountPercent: '0',
		publicNotes: '',
		privateNotes: '',
		paymentType: 'card',
		paymentDate: '',
		products: [emptyRow],
		subtotal: 0,
		taxTotal: 0,
		total: 0,
		discountValue: 0,
		dueDate: addDays(new Date(), 14),
		status: 'issued',
	};

	const [form, setForm] = useState(() => {
		const rows = baseForm.products?.length ? baseForm.products.map(recalcRow) : [emptyRow];
		const sums = recalcAll(rows, baseForm.discountPercent);
		return { ...baseForm, products: rows, ...sums };
	});

	const setField = useCallback((name, value) => {
		setForm(prev => {
			let next = { ...prev, [name]: value };
			if (name === 'discountPercent') {
				const sums = recalcAll(prev.products, value);
				next = { ...next, ...sums };
			}
			return next;
		});
	}, []);

	const setProduct = useCallback((idx, patch) => {
		setForm(prev => {
			const rows = prev.products.map((r, i) => (i === idx ? recalcRow({ ...r, ...patch }) : r));
			const discountPercent = prev.discountPercent ?? prev.discount ?? 0;
			const sums = recalcAll(rows, discountPercent);
			return { ...prev, products: rows, ...sums };
		});
	}, []);

	const addRow = useCallback(() => {
		setForm(prev => {
			const rows = [...prev.products, { ...emptyRow }];
			const discountPercent = prev.discountPercent ?? prev.discount ?? 0;
			const sums = recalcAll(rows, discountPercent);
			return { ...prev, products: rows, ...sums };
		});
	}, [emptyRow]);

	const removeRow = useCallback(idx => {
		setForm(prev => {
			const rows = prev.products.filter((_, i) => i !== idx);
			if (!rows.length) {
				// allow removing down to zero products; keep totals consistent
				const sums = recalcAll([], prev.discountPercent ?? prev.discount ?? 0);
				return { ...prev, products: [], ...sums };
			}
			const sums = recalcAll(rows, prev.discountPercent ?? prev.discount ?? 0);
			return { ...prev, products: rows, ...sums };
		});
	}, []);

	return { form, setForm, setField, setProduct, addRow, removeRow };
}
